import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Zap } from 'lucide-react';
import OptimizedImage from '@/components/OptimizedImage';
import api from '@/lib/api';

const ACCENT_COLORS = ['#3b82f6','#f5c518','#06b6d4','#a855f7','#ef4444','#22c55e','#f97316','#ec4899'];

function getAccent(index) {
  return ACCENT_COLORS[index % ACCENT_COLORS.length];
}

function isLightColor(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
}

export default function FeaturedStories() {
  const navigate = useNavigate();
  const [boostedItems, setBoostedItems] = useState([]);

  useEffect(() => {
    api.get('/items/boosted').then(r => setBoostedItems(r.data || [])).catch(() => {});
  }, []);

  // Only show if there are real boosted items
  if (boostedItems.length === 0) return null;

  const featured = boostedItems.slice(0, 8).map((item, i) => ({
    id: item.item_id,
    name: item.seller_name || '?',
    initial: (item.seller_name || '?').charAt(0),
    price: `$${item.price >= 1000 ? (item.price / 1000).toFixed(item.price >= 10000 ? 0 : 1) + 'k' : item.price}`,
    picture: item.images?.[0] || item.image_url || null,
    itemId: item.item_id,
    accent: i,
  }));

  const sora = { fontFamily: 'Sora, sans-serif' };

  return (
    <div data-testid="featured-stories" className="px-3 pt-2 pb-1" style={sora}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-xs font-bold tracking-wide" style={{ color: '#f5c518' }}>
          <Zap className="w-3 h-3 inline mr-0.5 -mt-0.5" />DESTACADOS
        </span>
      </div>

      {/* Stories row */}
      <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
        {featured.map((f, i) => {
          const accent = getAccent(f.accent ?? i);
          const light = isLightColor(accent);
          return (
            <button
              key={f.id}
              data-testid={`featured-story-${f.id}`}
              onClick={() => f.itemId ? navigate(`/item/${f.itemId}`) : null}
              className="shrink-0 flex flex-col items-center"
              style={{ width: 64 }}
            >
              {/* Circle */}
              <div className="relative" style={{ width: 56, height: 56 }}>
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center overflow-hidden"
                  style={{
                    border: `2.5px solid ${accent}`,
                    boxShadow: `0 0 18px ${accent}50`,
                    background: '#111',
                  }}
                >
                  {f.picture ? (
                    <OptimizedImage src={f.picture} alt="" thumbWidth={120} className="w-full h-full rounded-full" />
                  ) : (
                    <span className="text-sm font-bold" style={{ color: accent }}>{f.initial}</span>
                  )}
                </div>
                {/* Live dot */}
                <span
                  className="absolute"
                  style={{
                    top: 1, right: 1,
                    width: 11, height: 11,
                    borderRadius: '50%',
                    background: accent,
                    border: '2px solid #080808',
                  }}
                />
                {/* Price badge */}
                <span
                  className="absolute left-1/2 -translate-x-1/2 text-[9px] font-bold px-1.5 py-[1px] rounded-full whitespace-nowrap"
                  style={{
                    bottom: -4,
                    background: accent,
                    color: light ? '#000' : '#fff',
                    border: '2px solid #080808',
                    fontFamily: 'Sora, sans-serif',
                  }}
                >
                  {f.price}
                </span>
              </div>
              {/* Name */}
              <span
                className="mt-2 text-center truncate w-full block"
                style={{ color: '#777', fontSize: 8, maxWidth: 64, fontFamily: 'Sora, sans-serif' }}
              >
                {f.name}
              </span>
            </button>
          );
        })}

        {/* BOOST CTA */}
        <button
          data-testid="featured-boost-cta"
          onClick={() => navigate('/upload')}
          className="shrink-0 flex flex-col items-center"
          style={{ width: 64 }}
        >
          <div
            className="w-14 h-14 rounded-full flex flex-col items-center justify-center"
            style={{
              background: 'linear-gradient(135deg,#1a1400,#2a2000)',
              border: '2px dashed #f5c51840',
            }}
          >
            <Zap className="w-5 h-5" style={{ color: '#f5c518' }} />
            <span className="text-[7px] font-bold mt-0.5" style={{ color: '#f5c518', fontFamily: 'Sora, sans-serif' }}>BOOST</span>
          </div>
          <span
            className="mt-2 text-center block leading-tight"
            style={{ color: '#666', fontSize: 8, maxWidth: 64, fontFamily: 'Sora, sans-serif' }}
          >
            Destaca tu articulo!
          </span>
        </button>
      </div>
    </div>
  );
}
